import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { path, type, isEmpty, pathOr } from 'ramda';
import classNames from 'classnames/bind';

// Components
import {
  DataBlocks,
  Link,
  RouterLink,
  Status,
  I18n,
  ErrorBlock,
  Tooltip,
} from 'components';

// Utils
import printAccumulator from 'utils/accumulators';
import { formatDate, formatDateString } from 'utils/formatString';
import declint from 'utils/declint';

// Actions
import { openPopup } from 'actions';
import TariffConstructor from '../TariffConstructor';
import HomeAddress from '../HomeAddress';
import { refetchData } from '../../actions';

// Mappers
import {
  getServicesArrayLinks,
  getProgressBarColor,
  getAccumulatorsList,
  getAccumulatorName,
} from './mappers';

// Styles
import styles from './styles.pcss';
const cx = classNames.bind(styles);

class Convergence extends Component {
  getMobilePrice = () => {
    try {
      const {
        tariffRate,
        convergence: { internet, tv, insurance },
      } = this.props;
      return tariffRate - (internet.price || 0) - (tv.price || 0) - (insurance.price || 0);
    } catch (e) {
      return null;
    }
  };

  getMobileBlockPrice = () => {
    const { accumulators } = this.props;

    const { rcRateText: mobileRcRateText } = accumulators;
    const price = this.getMobilePrice();

    if (mobileRcRateText) {
      return (
        <div className={cx('price')}>
          <span dangerouslySetInnerHTML={{ __html: mobileRcRateText }} />
        </div>
      );
    }
    if (price) {
      return (
        <div className={cx('price')}>
          <I18n
            path="common.pricePerMonth"
            vars={{ sum: price }}
          />
        </div>
      );
    }
  };

  getRegionInfoBlock = (regionInfo) => (
    regionInfo && (
    <span>{regionInfo.regionName}</span>)
  );

  renderPriceWithPeriod = (price, period) => (
    <>
      {price}
      {' '}
      ₽
      {' '}
      {period}
    </>
  );

  renderInternetPrice = () => {
    const { convergence: { internet, pricePeriod } } = this.props;

    if (internet.price) {
      if (pricePeriod) {
        return (this.renderPriceWithPeriod(internet.price, pricePeriod));
      }

      return (
        <I18n
          path="common.pricePerMonth"
          vars={{ sum: internet.price }}
        />
      );
    }

    return 'Бесплатно';
  }

  renderTvPrice = () => {
    const { convergence: { tv, pricePeriod } } = this.props;

    if (tv.price) {
      const priceText = pricePeriod
        ? (this.renderPriceWithPeriod(tv.price, pricePeriod))
        : (
          <I18n
            path="common.pricePerMonth"
            vars={{ sum: tv.price }}
          />
        );

      return (
        <>
          {priceText}
          {tv.trialPriceText && (
            <Tooltip>
              {tv.trialPriceText}
            </Tooltip>
          )}
        </>
      );
    }
    return 'Бесплатно';
  }

  render() {
    const {
      accumulators,
      convergence,
      services,
      error,
      actions,
      isBlocked,
    } = this.props;

    if (error && error.type) {
      return (
        <ErrorBlock
          title={<I18n path="home.accumulators.blockTitle" />}
          refreshAction={() => actions.refetchData('Accumulators,Convergence')}
        />
      );
    }

    const {
      internet,
      tv,
      availableTv,
      insurance,
      pricePeriod,
      mobile,
      presetTag,
    } = convergence;

    const { resetDate, rcRateText: mobileRcRateText } = accumulators;
    const price = this.getMobilePrice();
    const mobileBlockPrice = this.getMobileBlockPrice(mobileRcRateText, price);
    const hideInternetBlocks = presetTag === 'Y';
    const accumulatorsList = this.props.getAccumulatorsList(accumulators.list);
    const dataBlocksLinks = this.props.getServicesArrayLinks(services);

    return (
      <>
        <div className={cx('container')} id="js-convergence-block">
          <DataBlocks.Block
            title={(
              <RouterLink className="link--hidden-visited-color" to="/services">
                <I18n path="home.accumulators.blockTitle" />
              </RouterLink>
            )}
            description={mobileBlockPrice}
            links={dataBlocksLinks}
            regionInfo={this.getRegionInfoBlock(mobile?.regionInfo)}
          >

            {type(accumulators.constructor) === 'Object' && !isEmpty(accumulators.constructor) && (
              <TariffConstructor
                checkStatusUrl={accumulators.constructor.checkStatusUrl}
                connectUrl={accumulators.constructor.connectUrl}
                selectorDataUrl={accumulators.constructor.selectorDataUrl}
                isDisabled={isBlocked}
                description={accumulators.changeTexts}
                className={cx('tariffConstructor')}
                isConvergent
              />
            )}
            {accumulatorsList && accumulatorsList.length > 0 && resetDate && (
              <div className={cx('accumulators')}>
                <div className={cx('accumulatorsTitle')}>
                  Осталось до
                  {' '}
                  {formatDate(resetDate)}
                  {':'}
                </div>
                {accumulatorsList.map((accumulator, index) => {
                  const isPresent = accumulator.presentType === 'UNL_PRES'
                    && accumulator.unlimExpirationDate && accumulator.isUnlimited;

                  return (
                    <div
                      key={`accumulator-unit-${accumulator.title}-${index}`}
                      className={cx('accumulator', { isPresent })}
                    >
                      <div className={cx('accumulatorName')}>
                        {this.props.getAccumulatorName(accumulator)}
                        {(accumulator.presentType !== 'UNL_PRES' && accumulator.description) && (
                          <Tooltip>
                            <div dangerouslySetInnerHTML={{ __html: accumulator.description }} />
                          </Tooltip>
                        )}
                      </div>
                      <div className={cx('accumulatorInfo')}>
                        <div className={cx('accumulatorRest')}>
                          {accumulator.isUnlimited
                            ? 'Безлимитно'
                            : printAccumulator(accumulator)}
                        </div>
                      </div>
                      {!accumulator.isUnlimited && (
                        <div className={cx('progressBar')}>
                          <div
                            className={cx('progressBarValue')}
                            style={{ width: `${accumulator.rest / accumulator.size * 100}%` }}
                          >
                            <div
                              className={cx(
                                'progressBarFill',
                                this.props.getProgressBarColor(accumulator, accumulators.progressBarLimits),
                              )}
                            />
                          </div>
                        </div>
                      )}
                      {accumulator.isLowValue && (
                        <Link href="https://beeline.ru/traffic/">Пополнить интернет</Link>
                      )}

                      {isPresent && (
                        <I18n
                          path="home.accumulators.unlimGiftedExpDate"
                          vars={{
                            date: formatDateString(accumulator.unlimExpirationDate, 'D.MM'),
                          }}
                          className={cx('unlimitedDate')}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </DataBlocks.Block>

          {!hideInternetBlocks && (
            <>
              {internet ? (
                <DataBlocks.Block
                  title={(
                    <RouterLink className="link--hidden-visited-color" to="/internet">
                      <I18n path="home.internet.blockTitle" />
                    </RouterLink>
                  )}
                  description={(
                    <div className={cx('price')}>
                      {this.renderInternetPrice()}
                    </div>
                  )}
                  links={[{
                    children: 'Подключить больше услуг',
                    href: internet.link,
                  }, internet.isSpeedUpAvailable ? {
                    children: 'Изменить скорость',
                    to: '/internet',
                  } : null]}
                  regionInfo={this.getRegionInfoBlock(internet.regionInfo)}
                >
                  <div className={cx('serviceParams')}>
                    <div className={cx('serviceParam')}>
                      <span className={cx('serviceParamLabel')}>
                        Скорость
                      </span>
                      <span className={cx('serviceParamValue')}>
                        {internet.maxSpeedUp / 1000}
                        {' '}
                        Мбит/с
                      </span>
                    </div>
                    {internet.routerCount > 0 && (
                      <div className={cx('serviceParam')}>
                        <span className={cx('serviceParamLabel')}>
                          Устройства
                        </span>
                        <span className={cx('serviceParamValue')}>
                          {declint(internet.routerCount, ['Wi-Fi роутер', '%s роутера', '%s роутеров'])}
                        </span>
                      </div>
                    )}
                    <div className={cx('serviceParam')}>
                      <span className={cx('serviceParamLabel')}>
                        Доп. услуги
                      </span>
                      <span className={cx('serviceParamValue')}>
                        {declint(internet.connectedServiceCount, [
                          '%s услуга',
                          '%s услуги',
                          '%s услуг',
                        ])}
                      </span>
                    </div>
                  </div>
                </DataBlocks.Block>
              ) : (
                <ErrorBlock
                  title={(
                    <RouterLink className="link--hidden-visited-color" to="/internet">
                      <I18n path="home.internet.blockTitle" />
                    </RouterLink>
                  )}
                  refreshAction={() => actions.refetchData('Convergence')}
                />
              )}
            </>
          )}

          {tv ? (
            <>
              {(tv.channelCount > 0 || tv.tvDevices > 0) ? (
                <DataBlocks.Block
                  title={(
                    <RouterLink to="/tv">
                      <I18n path="home.tv.blockTitle" />
                    </RouterLink>
                  )}
                  description={(
                    <div className={cx('price')}>
                      {this.renderTvPrice()}
                    </div>
                  )}
                  links={tv.channelCount ? (
                    [{
                      children: 'Подключить больше каналов',
                      href: tv.link,
                    }]
                  ) : (
                    [{
                      children: 'Подключить ещё устройства',
                      href: `${tv.link}#tv-devices`,
                    }]
                  )}
                  regionInfo={this.getRegionInfoBlock(tv.regionInfo)}
                >
                  <div className={cx('serviceParams')}>
                    <div className={cx('serviceParam')}>
                      <span className={cx('serviceParamLabel')}>
                        Каналы
                      </span>
                      <span className={cx('serviceParamValue')}>
                        {tv.channelCount ? (
                          declint(tv.channelCount, [
                            '%s ТВ-канал',
                            '%s ТВ-канала',
                            '%s ТВ-каналов',
                          ])
                        ) : (
                          <Link className="hideVisitedColor" href={`${tv.link}#tv-packages`}>
                            Подключить
                          </Link>
                        )}
                      </span>
                    </div>
                    <div className={cx('serviceParam')}>
                      <span className={cx('serviceParamLabel')}>
                        Устройства
                      </span>
                      <span className={cx('serviceParamValue')}>
                        {tv.tvDevices}
                      </span>
                    </div>
                    {tv.subscriptionCount ? (
                      <div className={cx('serviceParam')}>
                        <span className={cx('serviceParamLabel')}>
                          Подписки
                        </span>
                        <span className={cx('serviceParamValue')}>
                          {declint(tv.subscriptionCount, [
                            '%s ТВ-подписка',
                            '%s ТВ-подписки',
                            '%s ТВ-подписок',
                          ])}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </DataBlocks.Block>
              ) : (
                <DataBlocks.Block
                  isCenteredContent
                  title={tv.price ? (
                    <RouterLink className="link--hidden-visited-color" to="/internet">
                      <I18n path="home.tv.blockTitle" />
                    </RouterLink>
                  ) : null}
                  description={(
                    <div className={cx('price')}>
                      {tv.price > 0 && (
                        <>
                          {pricePeriod ? (
                            <>
                              {this.renderPriceWithPeriod(tv.price, pricePeriod)}
                            </>
                          ) : (
                            <I18n
                              path="common.pricePerMonth"
                              vars={{ sum: tv.price }}
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                >
                  <div className={cx('noService')}>
                    <Link className="hideVisitedColor" href={tv.link}>
                      Подключить
                      <br />
                      домашнее ТВ
                    </Link>
                  </div>

                  {availableTv && (
                    <div className={cx('availableTv')}>
                      {'От '}
                      {declint(availableTv.channelCount, [
                        '%s канала',
                        '%s каналов',
                        '%s каналов',
                      ])}
                      {' за '}
                      {pricePeriod ? (
                        <>
                          {this.renderPriceWithPeriod(availableTv.price, pricePeriod)}
                        </>
                      ) : (
                        <I18n
                          path="common.pricePerMonth"
                          vars={{ sum: availableTv.price }}
                        />
                      )}
                    </div>
                  )}
                </DataBlocks.Block>
              )}
            </>
          ) : (
            <ErrorBlock
              title={(
                <RouterLink to="/tv">
                  <I18n path="home.tv.blockTitle" />
                </RouterLink>
              )}
              refreshAction={() => actions.refetchData('Convergence')}
            />
          )}
        </div>

        {!hideInternetBlocks && <HomeAddress />}

        {insurance && (insurance.exists || insurance.price) ? (
          <DataBlocks.Block
            title={(
              <RouterLink className="link--hidden-visited-color" to="/insurance">
                <I18n path="home.insurance.blockTitle" />
              </RouterLink>
            )}
            info={(
              <div className={cx('blockInfo')}>
                Есть вопросы? Звоните по бесплатному номеру 8 (800) 700-8000.
                {' '}
                <Link href={insurance.link}>
                  К доступным сервисам страхования
                </Link>
              </div>
            )}
          >
            {insurance.exists && (
              <Status className={cx('insuranceStatus')}>
                Услуга отключена, но страховка ещё действует.
                {' '}
                <Link href={insurance.link}>
                  Включить
                </Link>
              </Status>
            )}
            {insurance.price ? (
              <DataBlocks.Table>
                <DataBlocks.TableRow>
                  <DataBlocks.TableCell className={cx('tableCell')}>
                    Стоимость услуги
                  </DataBlocks.TableCell>
                  <DataBlocks.TableCell className={cx('tableCell')}>
                    <I18n
                      path="common.pricePerMonth"
                      vars={{ sum: insurance.price }}
                    />
                  </DataBlocks.TableCell>
                  <DataBlocks.TableCell className={cx('tableCell')}>
                    <Status className={['success', 'compact']}>
                      Подключено
                    </Status>
                  </DataBlocks.TableCell>
                </DataBlocks.TableRow>
              </DataBlocks.Table>
            ) : null}
          </DataBlocks.Block>
        ) : null}
      </>
    );
  }
}

Convergence.propTypes = {
  tariffRate: PropTypes.number,
  accumulators: PropTypes.object,
  convergence: PropTypes.object,
  services: PropTypes.object,
  error: PropTypes.object,
  actions: PropTypes.object,
  isBlocked: PropTypes.bool,
  getServicesArrayLinks: PropTypes.func,
  getProgressBarColor: PropTypes.func,
  getAccumulatorsList: PropTypes.func,
  getAccumulatorName: PropTypes.func,
};

const mapStateToProps = ({ home }) => {
  const hasError = !home.isUserInfoFetching && home.userInfoFetchingError !== null;
  const status = path(['home', 'status', 'data', 'status'], home);

  if (hasError) {
    return {
      error: {
        type: 'Fetching user info error',
      },
    };
  }

  const tariffRcRateText = pathOr('', ['profileSummary', 'data', 'tariffRcRateText'], home);
  const accumulators = pathOr({}, ['accumulators', 'data'], home);
  const convergence = pathOr({}, ['convergence', 'data'], home);
  const services = pathOr({}, ['services', 'data'], home);
  const error = pathOr({}, ['convergence', 'error'], home);

  return {
    tariffRate: Number.parseInt(tariffRcRateText, 10),
    accumulators,
    convergence,
    services,
    error,
    isBlocked: status && status !== 'Active',
  };
};

const mapDispatchToProps = (dispatch) => ({
  getServicesArrayLinks,
  getProgressBarColor,
  getAccumulatorsList,
  getAccumulatorName,
  actions: bindActionCreators({
    openPopup,
    refetchData,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Convergence);
